import { useRef, useState } from 'react'
import gql from 'graphql-tag'
import { v4 as uuid } from 'uuid'
import { LlmChatMessageSenderType } from '@/__gen/types'
import { apolloClient } from '@/state/apollo-client'
import { useFormContext } from '@/state/main-form/reducer'
import { PollLlmChatQuery, usePollLlmChatLazyQuery, useSendMessageMutation } from './__gen/useLlm'

gql`
	mutation sendMessage(
		$id: ID!
		$content: String!
		$form: ID!
		$chat: ID!
		$recordId: ID!
	) {
		llmChatMessage_Update(
			input: {
				id: $id
				content: $content
				chat: $chat
				record: $recordId
				form: $form
			}
		) {
			object {
				id
				chat {
					id
				}
			}
			errors {
				field
				messages
			}
		}
	}
`

const GET_MESSAGES_QUERY = gql`
	query PollLlmChat($chatId: ID!) {
		llmChat(id: $chatId) {
			id
			created
			messages(orderBy: [{ field: created, direction: ASC }]) {
				id
				content
				created
				sender
				responseReceivedAt
			}
		}
	}
`


export const useLlm = () => {
	const [sendMessage, {loading: sending}] = useSendMessageMutation()
	const [get, { data }] = usePollLlmChatLazyQuery({})
	const formContext = useFormContext()
	const chatIdRef = useRef<string>(uuid())
	const [startingStream, setStartingStream] = useState(false)
	const variables = { chatId: chatIdRef.current }

	const addCacheMessage = ({id, content, sender}: {id: string, content: string, sender: LlmChatMessageSenderType}) => {
		const existing = apolloClient.readQuery<PollLlmChatQuery>({ query: GET_MESSAGES_QUERY, variables })
		apolloClient.writeQuery<PollLlmChatQuery>({
			query: GET_MESSAGES_QUERY,
			variables,
			data: {
				__typename: 'PharosQuery',
				llmChat: {
					__typename: 'LlmChatType',
					id: chatIdRef.current,
					created: existing?.llmChat?.created ?? new Date().toISOString(),
					messages: [
						...(existing?.llmChat?.messages ?? []),
						{
							__typename: 'LLmChatMessageType',
							id,
							content,
							created: new Date().toISOString(),
							responseReceivedAt: null,
							sender,
						},
					],
				},
			},
		})
	}

	const send = async ({ content }: { content: string }) => {
		const id = uuid()
		addCacheMessage({id, content, sender: LlmChatMessageSenderType.User})
		get({ variables, fetchPolicy: 'cache-only' })
		const resp = await sendMessage({
			variables: {
				id,
				chat: chatIdRef.current,
				content,
				form: formContext.form.id,
				recordId: formContext.record.id,
			},
		})

		if (resp.errors || !resp.data?.llmChatMessage_Update?.object) {
			return
		}

		const messageId = resp.data.llmChatMessage_Update.object.id
		setStartingStream(true)
		await streamResponse(messageId)
		
	}

	const streamResponse = (messageId: string) =>
		new Promise<void>((resolve) => {
			const id = uuid()
			addCacheMessage({id, content: '', sender: LlmChatMessageSenderType.Agent})
			const eventSource = new EventSource(`/api/v1/llm/messages/${messageId}/stream/${id}`)
			let accumulated = ''

			eventSource.onmessage = async (event) => {
				setStartingStream(false)
				if (event.data === '[DONE]') {
					eventSource.close()
					await get({ variables: { chatId: chatIdRef.current }, fetchPolicy: 'network-only' })
					resolve()
					return
				}
				try {
					const parsed = JSON.parse(event.data) as { text: string }
					accumulated += parsed.text
					apolloClient.writeFragment({
						id: `LLmChatMessageType:${id}`,
						fragment: gql`
							fragment StreamingMessage on LLmChatMessageType {
								content
							}
						`,
						data: { content: accumulated },
					})
				} catch {
					console.error('Malformed chunk', event.data)
					// ignore malformed chunks
				}
			}

			eventSource.onerror = () => {
				setStartingStream(false)
				eventSource.close()
				resolve()
			}
		})

	return {
		send,
		awaitingResponse: sending || startingStream,
		messages: data?.llmChat?.messages ?? [],
	}
}
